/**
 * PDUOutletMap
 * Visual representation of a PDU's outlets.
 * Shows each outlet as a "socket card" with its connection status,
 * the connected device name, and quick-edit for outlet metadata.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// ── Connection diagram ─────────────────────────────────────────────────────────

const CABLE_COLOR_HEX = {
  negro: '#475569', rojo: '#ef4444', azul: '#3b82f6', verde: '#22c55e',
  amarillo: '#eab308', blanco: '#e2e8f0', gris: '#94a3b8',
  naranja: '#f97316', morado: '#a855f7', rosa: '#ec4899',
};
function cableHex(c) { return c ? (CABLE_COLOR_HEX[c.toLowerCase()] || '#6366f1') : '#6366f1'; }

function PDUConnectionDiagram({ outlets }) {
  const connected = outlets.filter(o => o.connection);
  if (!connected.length) return (
    <div style={{ color: 'var(--text-muted)', fontSize: '0.83rem', fontStyle: 'italic', padding: '0.75rem 0' }}>
      Sin dispositivos conectados.
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', padding: '0.75rem 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: 2 }}>
        <div style={{ minWidth: 110, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', textAlign: 'center' }}>Enchufe PDU</div>
        <div style={{ flex: 1 }} />
        <div style={{ minWidth: 150, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', textAlign: 'center' }}>Dispositivo / PSU</div>
      </div>
      {connected.map(o => {
        const conn = o.connection;
        const color = cableHex(conn.cable_color);
        return (
          <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* Outlet node */}
            <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 6, padding: '0.3rem 0.6rem', fontSize: '0.78rem', color: '#10b981', fontWeight: 600, minWidth: 110, textAlign: 'center' }}>
              <code style={{ background: 'rgba(16,185,129,0.15)', padding: '1px 5px', borderRadius: 3, color: '#34d399', fontSize: '0.72rem' }}>#{o.outlet_number}</code>
              {o.outlet_type && <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 400, marginTop: 1 }}>{o.outlet_type}</div>}
              {o.phase && <div style={{ fontSize: '0.62rem', color: '#818cf8', marginTop: 1 }}>{o.phase}</div>}
            </div>
            {/* Cable */}
            <div style={{ flex: 1, position: 'relative', height: 28, display: 'flex', alignItems: 'center' }}>
              <div style={{ height: 2, width: '100%', background: `linear-gradient(90deg, rgba(16,185,129,0.5), ${color}, rgba(99,102,241,0.5))`, borderRadius: 1 }} />
              {(conn.cable_type || conn.cable_color) && (
                  <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', background: 'var(--bg-surface)', border: `1px solid ${color}`, borderRadius: 3, padding: '1px 6px', fontSize: '0.67rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  {[conn.cable_type, conn.cable_color].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
            {/* Device node */}
            <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 6, padding: '0.3rem 0.6rem', fontSize: '0.78rem', color: '#818cf8', fontWeight: 600, minWidth: 150, textAlign: 'center' }}>
              {conn.consumer_name || '?'}
              {(conn.psu_label || conn.psu_component_model) && (
                <div style={{ fontSize: '0.67rem', color: 'var(--text-muted)', fontWeight: 400, marginTop: 1 }}>
                  ⚡ {conn.psu_label || conn.psu_component_model}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────

const OUTLET_TYPE_LABELS = {
  C13:    'IEC C13',
  C19:    'IEC C19',
  Schuko: 'Schuko',
  UK:     'UK',
  IEC309: 'IEC 309',
};

function OutletCard({ outlet, onEdit }) {
  const conn   = outlet.connection;
  const isUsed = Boolean(conn || outlet.child_pdu_id);
  const childName = outlet.child_pdu_name;

  return (
    <div
      title={isUsed ? `Conectado a: ${childName || conn?.consumer_name || '?'} — ${conn?.psu_label || ''}`.trim() : 'Libre'}
      onClick={() => onEdit(outlet)}
      style={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        gap:            4,
        padding:        '0.5rem 0.4rem',
        border:         `2px solid ${isUsed ? '#4ade80' : 'var(--border)'}`,
        borderRadius:   8,
        background:     isUsed ? 'rgba(74,222,128,0.08)' : 'var(--bg-surface)',
        cursor:         'pointer',
        minWidth:       72,
        maxWidth:       88,
        flex:           '1 0 72px',
        transition:     'border-color 0.15s',
      }}
    >
      <span style={{ fontSize: '0.68rem', fontWeight: 700, color: isUsed ? '#4ade80' : 'var(--text-muted)', letterSpacing: 0.5 }}>
        #{outlet.outlet_number}
      </span>
      <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>
        {isUsed ? '🔌' : '⬜'}
      </span>
      <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
        {outlet.label && outlet.label !== `Outlet ${outlet.outlet_number}` ? outlet.label : outlet.outlet_type || '—'}
      </span>
      {isUsed && (
        <span style={{ fontSize: '0.65rem', color: '#4ade80', fontWeight: 600, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
          {childName || conn?.consumer_name || '?'}
        </span>
      )}
      {outlet.phase && (
        <span style={{ fontSize: '0.6rem', background: 'rgba(99,102,241,0.18)', color: '#818cf8', borderRadius: 3, padding: '0 3px' }}>
          {outlet.phase}
        </span>
      )}
    </div>
  );
}


// ── edit modal ────────────────────────────────────────────────────────────────

function OutletEditModal({ outlet, pduId, companyId, onClose, onSaved }) {
  const [form, setForm] = useState({
    label:       outlet.label        || '',
    outlet_type: outlet.outlet_type  || '',
    phase:       outlet.phase        || '',
    circuit_id:  outlet.circuit_id   || '',
    amperage:    outlet.amperage     ?? 16,
    is_active:   outlet.is_active    ?? true,
    notes:       outlet.notes        || '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.updatePduOutlet(pduId, outlet.id, form, companyId);
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '1.5rem', width: 380, display: 'flex', flexDirection: 'column', gap: '0.75rem', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <strong style={{ fontSize: '0.95rem', color: 'var(--text-primary)' }}>Enchufe #{outlet.outlet_number}</strong>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.3)', padding: '0.4rem 0.6rem', borderRadius: 5, fontSize: '0.8rem' }}>⚠ {error}</div>
        )}

        <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
          Etiqueta
          <input type="text" value={form.label} onChange={e => set('label', e.target.value)}
            style={{ display: 'block', width: '100%', marginTop: 3, padding: '0.3rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }} />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
            Tipo de enchufe
            <select value={form.outlet_type} onChange={e => set('outlet_type', e.target.value)}
              style={{ display: 'block', width: '100%', marginTop: 3, padding: '0.3rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }}>
              <option value="">— No especificado —</option>
              {Object.entries(OUTLET_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
            Fase
            <select value={form.phase} onChange={e => set('phase', e.target.value)}
              style={{ display: 'block', width: '100%', marginTop: 3, padding: '0.3rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }}>
              <option value="">—</option>
              <option value="L1">L1</option><option value="L2">L2</option><option value="L3">L3</option>
            </select>
          </label>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
            Circuito eléctrico
            <input type="text" value={form.circuit_id} onChange={e => set('circuit_id', e.target.value)} placeholder="C1, A3…"
              style={{ display: 'block', width: '100%', marginTop: 3, padding: '0.3rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }} />
          </label>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
            Amperaje máx.
            <input type="number" value={form.amperage} onChange={e => set('amperage', parseFloat(e.target.value))} min={1} step={0.5}
              style={{ display: 'block', width: '100%', marginTop: 3, padding: '0.3rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }} />
          </label>
        </div>

        <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
          Notas
          <input type="text" value={form.notes} onChange={e => set('notes', e.target.value)}
            style={{ display: 'block', width: '100%', marginTop: 3, padding: '0.3rem 0.5rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.82rem', color: 'var(--text-primary)' }} />
        </label>

        <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
          Enchufe activo
        </label>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose}
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.4rem 0.8rem', fontSize: '0.82rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancelar</button>
          <button type="button" onClick={handleSave} disabled={saving}
            style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, padding: '0.4rem 0.9rem', fontSize: '0.82rem', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}


// ── main component ────────────────────────────────────────────────────────────

export default function PDUOutletMap({ pduId, companyId }) {
  const [pdu,           setPdu]           = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [editingOutlet, setEditingOutlet] = useState(null);
  const [viewMode,      setViewMode]      = useState('grid'); // 'grid' | 'diagram'

  // Strip-level (PDU itself) editing state
  const [editingStrip,  setEditingStrip]  = useState(false);
  const [stripForm,     setStripForm]     = useState({ pdu_phase: '', pdu_amperage: '', parent_pdu_id: '', upstream_outlet_id: '' });
  const [stripSaving,   setStripSaving]   = useState(false);
  const [stripError,    setStripError]    = useState(null);
  const [rackPdus,      setRackPdus]      = useState([]);   // other PDUs in same rack
  const [parentOutlets, setParentOutlets] = useState([]);   // outlets of the selected parent PDU

  const load = useCallback(async () => {
    if (!pduId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getPdu(pduId, companyId);
      setPdu(res.data);
      setStripForm({
        pdu_phase:         res.data.pdu_phase         || '',
        pdu_amperage:      res.data.pdu_amperage != null ? String(res.data.pdu_amperage) : '',
        parent_pdu_id:     res.data.parent_pdu_id != null ? String(res.data.parent_pdu_id) : '',
        upstream_outlet_id: res.data.upstream_outlet_id != null ? String(res.data.upstream_outlet_id) : '',
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [pduId, companyId]);

  useEffect(() => { load(); }, [load]);

  // Load sibling PDUs in the same rack and the SAIs of the company for the
  // upstream-source selector (a PDU can be fed from another PDU or from a SAI).
  useEffect(() => {
    if (!companyId) return;
    const pdusPromise = pdu?.rack_id
      ? api.getPdus(companyId, pdu.rack_id)
      : Promise.resolve({ data: [] });
    Promise.all([
      pdusPromise,
      api.getUpsList(companyId).catch(() => ({ data: [] })),
    ])
      .then(([pduRes, upsRes]) => {
        const pdus = (pduRes.data || [])
          .filter(p => p.id !== pduId)
          .map(p => ({ ...p, _kind: 'pdu' }));
        const sais = (upsRes.data || [])
          .filter(u => u.rack_element_id && u.rack_element_id !== pduId)
          .map(u => ({
            id: u.rack_element_id,
            name: u.name,
            _kind: 'ups',
            rack_id: u.rack_id,
          }));
        setRackPdus([...pdus, ...sais]);
      })
      .catch(() => setRackPdus([]));
  }, [pdu?.rack_id, pduId, companyId]);

  // Load the outlets of the selected parent PDU so the user can pick one
  useEffect(() => {
    const parentId = stripForm.parent_pdu_id;
    if (!parentId) { setParentOutlets([]); return; }
    api.getPduOutlets(parseInt(parentId, 10), companyId)
      .then(res => setParentOutlets(res.data || []))
      .catch(() => setParentOutlets([]));
  }, [stripForm.parent_pdu_id, companyId]);

  const saveStrip = async () => {
    setStripSaving(true);
    setStripError(null);
    try {
      await api.updatePdu(pduId, {
        pdu_phase:          stripForm.pdu_phase    || null,
        pdu_amperage:       stripForm.pdu_amperage !== '' ? parseFloat(stripForm.pdu_amperage) : null,
        parent_pdu_id:      stripForm.parent_pdu_id !== '' ? parseInt(stripForm.parent_pdu_id, 10) : null,
        upstream_outlet_id: stripForm.upstream_outlet_id !== '' ? parseInt(stripForm.upstream_outlet_id, 10) : null,
      }, companyId);
      setEditingStrip(false);
      await load();
    } catch (e) {
      setStripError(e.message);
    } finally {
      setStripSaving(false);
    }
  };

  if (!pduId) {
    return <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Guarda la PDU primero para ver los enchufes.</div>;
  }
  if (loading) return <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Cargando enchufes…</div>;
  if (error)   return <div style={{ color: '#ef4444', fontSize: '0.85rem' }}>⚠ {error}</div>;
  if (!pdu)    return null;

  const outlets    = pdu.outlets || [];
  const usedCount  = outlets.filter(o => o.connection || o.child_pdu_id).length;
  const totalCount = outlets.length;
  const loadPct    = totalCount > 0 ? Math.round(usedCount / totalCount * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* ── PDU Strip Info ── */}
      <div style={{ background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, padding: '0.7rem 0.9rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: editingStrip ? 8 : 0 }}>
          <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#818cf8' }}>⚡ Circuito alimentador</span>
          <button type="button" onClick={() => { setEditingStrip(p => !p); setStripError(null); }}
            style={{ background: 'none', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 5, fontSize: '0.75rem', color: '#818cf8', padding: '2px 8px', cursor: 'pointer' }}>
            {editingStrip ? '✕ Cerrar' : '✏️ Editar'}
          </button>
        </div>

        {!editingStrip ? (
          // Read-only view
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 4 }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Fase: <b style={{ color: 'var(--text-primary)' }}>{pdu.pdu_phase || '—'}</b>
            </span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Amperios: <b style={{ color: 'var(--text-primary)' }}>{pdu.pdu_amperage != null ? `${pdu.pdu_amperage} A` : '—'}</b>
            </span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Fuente aguas arriba: <b style={{ color: 'var(--text-primary)' }}>
                {pdu.parent_pdu_id
                  ? (rackPdus.find(p => p.id === pdu.parent_pdu_id)?.name || `#${pdu.parent_pdu_id}`)
                  : '— (sin fuente aguas arriba)'}
              </b>
            </span>
            {pdu.parent_pdu_id && (
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Enchufe de entrada:{' '}
                {pdu.upstream_outlet_number != null ? (
                  <b style={{ color: 'var(--text-primary)' }}>#{pdu.upstream_outlet_number}</b>
                ) : (
                  <b style={{ color: '#f59e0b' }}>⚠ no especificado — pulsa Editar para seleccionarlo</b>
                )}
              </span>
            )}
          </div>
        ) : (
          // Edit form
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            {stripError && (
              <div style={{ background: 'rgba(239,68,68,0.1)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.3)', padding: '0.35rem 0.6rem', borderRadius: 5, fontSize: '0.78rem' }}>⚠ {stripError}</div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
              <label style={{ fontSize: '0.77rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                Fase
                <select value={stripForm.pdu_phase} onChange={e => setStripForm(f => ({ ...f, pdu_phase: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 2, padding: '0.28rem 0.45rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.8rem', color: 'var(--text-primary)' }}>
                  <option value="">— No especificada —</option>
                  {['L1', 'L2', 'L3', 'N'].map(ph => <option key={ph} value={ph}>{ph}</option>)}
                </select>
              </label>
              <label style={{ fontSize: '0.77rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                Amperios del circuito
                <input type="number" value={stripForm.pdu_amperage} onChange={e => setStripForm(f => ({ ...f, pdu_amperage: e.target.value }))} min={1} step={0.5} placeholder="ej. 16"
                  style={{ display: 'block', width: '100%', marginTop: 2, padding: '0.28rem 0.45rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.8rem', color: 'var(--text-primary)' }} />
              </label>
              <label style={{ fontSize: '0.77rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                Fuente aguas arriba (PDU / SAI)
                <select value={stripForm.parent_pdu_id} onChange={e => setStripForm(f => ({ ...f, parent_pdu_id: e.target.value, upstream_outlet_id: '' }))}
                  style={{ display: 'block', width: '100%', marginTop: 2, padding: '0.28rem 0.45rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.8rem', color: 'var(--text-primary)' }}>
                  <option value="">— Sin fuente aguas arriba —</option>
                  {rackPdus.map(p => (
                    <option key={p.id} value={p.id}>
                      {p._kind === 'ups' ? 'SAI · ' : ''}{p.name}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: '0.77rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                Enchufe de entrada
                {stripForm.parent_pdu_id && parentOutlets.length === 0 ? (
                  <div style={{ marginTop: 4, padding: '0.3rem 0.5rem', borderRadius: 5, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', fontSize: '0.73rem', color: '#f59e0b', lineHeight: 1.4 }}>
                    ⚠ La fuente aguas arriba no tiene enchufes configurados.
                  </div>
                ) : (
                  <select value={stripForm.upstream_outlet_id} onChange={e => setStripForm(f => ({ ...f, upstream_outlet_id: e.target.value }))} disabled={!stripForm.parent_pdu_id}
                    style={{ display: 'block', width: '100%', marginTop: 2, padding: '0.28rem 0.45rem', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.8rem', color: 'var(--text-primary)', opacity: stripForm.parent_pdu_id ? 1 : 0.5 }}>
                    <option value="">— No especificado —</option>
                    {parentOutlets.map(o => (
                      <option key={o.id} value={o.id}>
                        #{o.outlet_number}{o.label ? ` · ${o.label}` : ''}{o.outlet_type ? ` (${o.outlet_type})` : ''}{o.occupied && String(o.id) !== stripForm.upstream_outlet_id ? ' ⚠ ocupado' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </label>
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setEditingStrip(false); setStripError(null); }}
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 5, padding: '0.3rem 0.7rem', fontSize: '0.78rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancelar</button>
              <button type="button" onClick={saveStrip} disabled={stripSaving}
                style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 5, padding: '0.3rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: stripSaving ? 'not-allowed' : 'pointer' }}>{stripSaving ? 'Guardando…' : 'Guardar'}</button>
            </div>
          </div>
        )}
      </div>

      {/* Summary bar + view toggle */}
      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#6366f1' }}>{usedCount}</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>usados</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-muted)' }}>{totalCount - usedCount}</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>libres</span>
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <div style={{ height: 10, background: 'var(--border)', borderRadius: 5, overflow: 'hidden' }}>
            <div style={{ width: `${loadPct}%`, height: '100%', background: loadPct > 80 ? '#ef4444' : loadPct > 60 ? '#f59e0b' : '#22c55e', transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{loadPct}% ocupado</span>
        </div>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Haz clic en un enchufe para editar sus propiedades
        </span>

        {/* View toggle */}
        <div style={{ marginLeft: 'auto', display: 'flex', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {[['grid', '⬛ Cuadrícula'], ['diagram', '⚡ Diagrama']].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setViewMode(key)}
              style={{ background: viewMode === key ? 'rgba(99,102,241,0.2)' : 'transparent', color: viewMode === key ? '#818cf8' : 'var(--text-secondary)', border: 'none', padding: '0.25rem 0.75rem', fontSize: '0.78rem', fontWeight: viewMode === key ? 600 : 400, cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Outlet grid or diagram */}
      {viewMode === 'diagram' ? (
        <PDUConnectionDiagram outlets={outlets} />
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {outlets.map(outlet => (
            <OutletCard key={outlet.id} outlet={outlet} onEdit={setEditingOutlet} />
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editingOutlet && (
        <OutletEditModal
          outlet={editingOutlet}
          pduId={pduId}
          companyId={companyId}
          onClose={() => setEditingOutlet(null)}
          onSaved={async () => { setEditingOutlet(null); await load(); }}
        />
      )}
    </div>
  );
}
